import { useEffect, useState } from "react";

/** Matches the Drawer's slide-out transition so content stays visible while the sheet closes. */
const CLOSE_TRANSITION_MS = 400;

/**
 * Whether the sheet body should be rendered: while it is open, and for the close transition after it closes.
 * The Drawer keeps its panel mounted off-screen when closed, so without this every meter card would re-render
 * on each snapshot even though nothing is visible.
 */
export default function useSheetContentMounted({ open }: { open: boolean }): boolean {
  const [stateIsOpenLastSeen, setStateIsOpenLastSeen] = useState(open);
  const [stateIsClosing, setStateIsClosing] = useState(false);

  // Flip to "closing" in the same render `open` drops, so the cards never unmount before the slide-out starts.
  if (open !== stateIsOpenLastSeen) {
    setStateIsOpenLastSeen(open);
    setStateIsClosing(!open);
  }

  useEffect(() => {
    if (open || !stateIsClosing) {
      return;
    }
    const closeTimeout = setTimeout(() => setStateIsClosing(false), CLOSE_TRANSITION_MS);
    return () => {
      clearTimeout(closeTimeout);
    };
  }, [open, stateIsClosing]);

  return open || stateIsClosing;
}
